import { useEffect, useLayoutEffect, useMemo, useRef, type RefObject } from "react";
import { getUsageMeter } from "./usage";

export function useUsage(sessionId: string, video: RefObject<HTMLVideoElement | null>, visibleTV: boolean, guideChannel: boolean, archival: boolean) {
  const meter = useMemo(() => getUsageMeter(sessionId), [sessionId]);
  const state = useRef({ visibleTV, guideChannel, archival });
  const playing = useRef(false);
  const progress = useRef({ time: -1, observed: 0 });
  const sampleState = () => {
    const element = video.current;
    const now = performance.now();
    if (element && element.currentTime !== progress.current.time) progress.current = { time: element.currentTime, observed: now };
    meter.update({ visible: state.current.visibleTV && document.visibilityState !== "hidden", guide: state.current.guideChannel,
      playing: state.current.archival && playing.current && now - progress.current.observed < 2500 && Boolean(element && !element.paused && !element.ended && element.readyState >= 3) });
  };
  const send = async (keepalive = false) => {
    meter.sample(); meter.flush();
    if (meter.sending || !meter.queue.length) return;
    const events = meter.queue.slice(0, 20);
    meter.sending = true;
    try {
      const response = await fetch("/v1/events/usage", { method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" }, body: JSON.stringify({ session_id: meter.sessionId, events }), keepalive, signal: AbortSignal.timeout(8000) });
      if (response.ok) meter.acknowledge(new Set(events.map(event => event.id)));
    } catch { /* Best effort; retry the same IDs, never interrupt television. */ }
    finally { meter.sending = false; }
  };
  useLayoutEffect(() => {
    meter.sample(); meter.flush();
    state.current = { visibleTV, guideChannel, archival }; sampleState();
    void send();
  }, [visibleTV, guideChannel, archival]);
  useEffect(() => {
    const element = video.current;
    const onPlaying = () => { sampleState(); playing.current = true; sampleState(); };
    const onStopped = () => { sampleState(); playing.current = false; sampleState(); };
    const visibility = () => { sampleState(); void send(true); };
    const exit = () => { sampleState(); meter.update({ visible: false, playing: false, guide: false }); void send(true); };
    const resume = () => { sampleState(); };
    const sample = window.setInterval(sampleState, 1000);
    const batch = window.setInterval(() => void send(), 30_000);
    element?.addEventListener("playing", onPlaying);
    for (const name of ["waiting", "pause", "ended", "emptied", "seeking", "error"]) element?.addEventListener(name, onStopped);
    document.addEventListener("visibilitychange", visibility);
    window.addEventListener("pagehide", exit); window.addEventListener("pageshow", resume);
    sampleState();
    return () => {
      meter.sample(); meter.flush(); meter.update({ visible: false, playing: false, guide: false });
      clearInterval(sample); clearInterval(batch);
      element?.removeEventListener("playing", onPlaying);
      for (const name of ["waiting", "pause", "ended", "emptied", "seeking", "error"]) element?.removeEventListener(name, onStopped);
      document.removeEventListener("visibilitychange", visibility); window.removeEventListener("pagehide", exit); window.removeEventListener("pageshow", resume);
    };
  }, [meter]);
  return meter;
}
